import type { ProjectSnapshot, EstimateItem } from '../model/types';
import type { Room } from '@/shared/model';
import { calculateProjectTotal } from './calculator';
import { calculateRoomGeometry } from '@/shared/lib/geometry';
import { get, set } from 'idb-keyval';

/**
 * Логика создания снимка (Snapshot) проекта.
 * Инкапсулирует сборку данных и расчет базовой статистики.
 */
export const createProjectSnapshot = (
    rooms: Room[], 
    activeRoomId: string | null, 
    safetyMargin: number, 
    name?: string,
    projectItems: EstimateItem[] = [],
    projectOverrides: ProjectSnapshot['projectOverrides'] = {}
): ProjectSnapshot => {
    const now = new Date();
    
    const stats = {
        total: calculateProjectTotal(rooms, safetyMargin), // will be enhanced in calculator for globals
        roomsCount: rooms.length,
        area: rooms.reduce((sum, r) => {
            const g = calculateRoomGeometry(
                r.dims?.length || 0, 
                r.dims?.width || 0, 
                r.dims?.height || 0,
                r.openings || [],
                r.partitions || [],
                r.wallElements || []
            );
            return sum + g.floorArea;
        }, 0),
        globalItemsCount: projectItems.length
    };

    return {
        id: Date.now(),
        name: name || `Проект ${now.toLocaleDateString()}`,
        lastModified: now.toISOString(),
        rooms,
        projectItems,
        projectOverrides,
        activeRoomId,
        safetyMargin,
        version: '1.3', // bumped for projectItems + overrides (global layer)
        stats
    };
};

const LIBRARY_KEY = 'bg2_library';

/**
 * Получить библиотеку проектов из хранилища (idb preferred).
 */
export const getProjectLibrary = async (): Promise<ProjectSnapshot[]> => {
    try {
        const data = await get(LIBRARY_KEY);
        return data ? data : [];
    } catch (e) {
        console.error('Failed to load library:', e);
        return [];
    }
};

/**
 * Сохранить библиотеку проектов в хранилище (idb).
 */
export const saveProjectLibrary = async (library: ProjectSnapshot[]) => {
    try {
        await set(LIBRARY_KEY, library);
        // Trigger event for hooks to react (for cross-tab)
        window.dispatchEvent(new Event('storage'));
    } catch (e) {
        console.error('Failed to save library:', e);
    }
};

/**
 * Immutable update of a single project entry in the library + unified signal for cross-tab (hooks).
 * Used by zustand store for commitVersion / autosave to keep active state in sync with library list.
 */
export async function updateProjectEntry(
  id: string | number,
  updater: (p: ProjectSnapshot) => Partial<ProjectSnapshot>
): Promise<void> {
  try {
    const lib = await getProjectLibrary();
    const idx = lib.findIndex((p) => String(p.id) === String(id));
    if (idx === -1) return;
    const now = new Date().toISOString();
    const updated = lib.map((p, i) =>
      i === idx ? { ...p, ...updater(p), lastModified: now } : p
    );
    await set(LIBRARY_KEY, updated);
    // Signal for useProjectLibrary hook (same-tab ignore not needed here; cross-tab via storage event)
    localStorage.setItem('__project_lib_signal', Date.now().toString());
    window.dispatchEvent(new Event('storage'));
  } catch (e) {
    console.error('Failed to update project entry:', e);
  }
}
